package dev.mmartos.advent.screen.day01

import advent_of_code_compose_2025.composeapp.generated.resources.Res
import advent_of_code_compose_2025.composeapp.generated.resources.check_mark
import advent_of_code_compose_2025.composeapp.generated.resources.right_arrow
import advent_of_code_compose_2025.composeapp.generated.resources.source_code_pro_regular
import androidx.compose.animation.core.Animatable
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement.spacedBy
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.center
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.graphics.drawscope.scale
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.font.toFontFamily
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.dp
import dev.mmartos.advent.models.DayDetails
import dev.mmartos.advent.theme.AoCTheme
import dev.mmartos.advent.ui.AutoScrollingTitledList
import dev.mmartos.advent.ui.AutoScrollingTitledListLayout
import dev.mmartos.advent.ui.CurrentElement
import dev.mmartos.advent.ui.CurrentElementLayout
import dev.mmartos.advent.ui.DayScaffold
import dev.mmartos.advent.ui.ParserSection
import dev.mmartos.advent.ui.Solution
import dev.mmartos.advent.ui.SolutionLayout
import dev.mmartos.advent.ui.SolverSection
import dev.mmartos.advent.ui.Title
import kotlinx.collections.immutable.PersistentList
import org.jetbrains.compose.resources.Font
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.ui.tooling.preview.Preview
import org.koin.compose.viewmodel.koinViewModel

@Composable
@Suppress("DuplicatedCode")
fun Day01Screen(
    dayDetails: DayDetails,
    puzzleInput: PersistentList<String>,
    onBackClicked: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val vm: Day01ViewModel = koinViewModel()
    val uiState by vm.uiState.collectAsState()
    DayScaffold(
        dayDetails = dayDetails,
        puzzleInput = puzzleInput,
        uiState = uiState,
        onStart = { input -> vm.startParser(input) },
        onBackClicked = onBackClicked,
        onDispose = { vm.stop() },
        parserContent = { parserStage, modifier ->
            ParserSection(
                parserStage = parserStage,
                modifier = modifier,
            )
        },
        solverContent1 = { solverStage, modifier ->
            Solver1Section(
                solverStage = solverStage,
                modifier = modifier,
            )
        },
        solverContent2 = { solverStage, modifier ->
            Solver2Section(
                solverStage = solverStage,
                modifier = modifier,
            )
        },
        modifier = modifier,
        parsingHeight = 360.dp,
        solvingHeight = 480.dp,
    )
}

@Composable
private fun ParserSection(
    parserStage: ParserStage,
    modifier: Modifier = Modifier,
) {
    ParserSection(
        parserStage = parserStage,
        modifier = modifier,
        parsingContent = { parsingStage: ParserStage.Parsing, modifier: Modifier ->
            ParsingContent(
                parserStage = parsingStage,
                modifier = modifier
            )
        },
        parsedContent = { parsedStage: ParserStage.Parsed, modifier: Modifier ->
            ParsedContent(
                parserStage = parsedStage,
                modifier = modifier
            )
        },
    )
}

@Composable
private fun ParsingContent(
    parserStage: ParserStage.Parsing,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier,
        horizontalArrangement = spacedBy(16.dp),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth()
        ) {
            CurrentElement(
                title = "Current line:",
                currentItem = parserStage.currentLine,
                layout = CurrentElementLayout.Horizontal,
            )
            Spacer(modifier = Modifier.height(8.dp))
            DialMovements(
                dialMovements = parserStage.dialMovements,
                modifier = Modifier.fillMaxSize(),
            )
        }
    }
}

@Composable
private fun ParsedContent(
    parserStage: ParserStage.Parsed,
    modifier: Modifier = Modifier
) {
    DialMovements(
        dialMovements = parserStage.dialMovements,
        modifier = modifier.fillMaxSize(),
    )
}

@Composable
private fun DialMovements(
    dialMovements: PersistentList<DialMovement>,
    modifier: Modifier = Modifier,
) {
    AutoScrollingTitledList(
        items = dialMovements,
        layout = AutoScrollingTitledListLayout.GridLayoutTitled(columns = 9),
        modifier = modifier,
        title = { Text("Dial Movements:") },
        itemContent = { item ->
            Row(
                horizontalArrangement = spacedBy(4.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Image(
                    painter = painterResource(item.direction.toDrawable()),
                    contentDescription = null,
                    colorFilter = ColorFilter.tint(Color.White),
                    modifier = Modifier.size(20.dp)
                )
                Text(
                    text = "${item.steps}",
                    style = MaterialTheme.typography.bodyLarge.copy(fontFamily = Font(Res.font.source_code_pro_regular).toFontFamily()),
                )
            }
        }
    )
}

@Composable
private fun Solver1Section(
    solverStage: SolverStage1,
    modifier: Modifier = Modifier,
) {
    SolverSection(
        solverStage = solverStage,
        modifier = modifier
            .fillMaxSize(),
    ) { solverStage ->
        val markerPosition = remember { Animatable(50f) }
        val currentDialMovement = (solverStage as? SolverStage1.Solving)?.currentDialMovement
        val currentDialerState = (solverStage as? SolverStage1.Solving)?.currentDialerState
            ?: (solverStage as? SolverStage1.Solved)?.currentDialerState
            ?: DialerState(currentValue = 50)
        val currentSolution = (solverStage as? SolverStage1.Solving)?.partialSolution
            ?: (solverStage as? SolverStage1.Solved)?.solution
            ?: "Unknown"
        val targetValue = if (currentDialMovement != null) {
            (currentDialerState.currentValue + currentDialMovement.effectiveMovement())
        } else {
            currentDialerState.currentValue
        }
        if (currentDialMovement != null) {
            DialMovement(
                currentDialMovement,
                modifier = Modifier
                    .fillMaxWidth()
                    .align(Alignment.CenterHorizontally),
            )
        }
        LaunchedEffect(solverStage) {
            markerPosition.snapTo(
                targetValue = targetValue.toFloat(),
            )
        }
        Dialer(
            markerPosition = markerPosition.value,
            modifier = Modifier
                .weight(1f)
                .aspectRatio(ratio = 1f, matchHeightConstraintsFirst = true)
                .align(Alignment.CenterHorizontally),
        )
        Solution(
            solution = currentSolution,
            partial = solverStage is SolverStage1.Solving,
            layout = SolutionLayout.Horizontal,
            solutionTextStyle = MaterialTheme.typography.titleLarge,
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.CenterHorizontally),
        )
    }
}

@Composable
private fun Solver2Section(
    solverStage: SolverStage2,
    modifier: Modifier = Modifier,
) {
    SolverSection(
        solverStage = solverStage,
        modifier = modifier
            .fillMaxSize(),
    ) { solverStage ->
        val markerPosition = remember { Animatable(50f) }
        val currentDialMovement = (solverStage as? SolverStage2.Solving)?.currentDialMovement
        val currentDialerState = (solverStage as? SolverStage2.Solving)?.currentDialerState
            ?: (solverStage as? SolverStage2.Solved)?.currentDialerState
            ?: DialerState(currentValue = 50)
        val currentSolution = (solverStage as? SolverStage2.Solving)?.partialSolution
            ?: (solverStage as? SolverStage2.Solved)?.solution
            ?: "Unknown"
        val targetValue = if (currentDialMovement != null) {
            (currentDialerState.currentValue + currentDialMovement.effectiveMovement())
        } else {
            currentDialerState.currentValue
        }
        if (currentDialMovement != null) {
            DialMovement(
                currentDialMovement,
                modifier = Modifier
                    .fillMaxWidth()
                    .align(Alignment.CenterHorizontally),
            )
        }
        LaunchedEffect(solverStage) {
            markerPosition.snapTo(
                targetValue = targetValue.toFloat(),
            )
        }
        Dialer(
            markerPosition = markerPosition.value,
            modifier = Modifier
                .weight(1f)
                .aspectRatio(ratio = 1f, matchHeightConstraintsFirst = true)
                .align(Alignment.CenterHorizontally),
        )
        Solution(
            solution = currentSolution,
            partial = solverStage is SolverStage2.Solving,
            layout = SolutionLayout.Horizontal,
            solutionTextStyle = MaterialTheme.typography.titleLarge,
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.CenterHorizontally),
        )
    }
}

@Composable
@ReadOnlyComposable
private fun SolverStage2.resolveSectionTitle(): Title =
    when (this) {
        is SolverStage2.Solving -> Title(
            icon = Res.drawable.right_arrow,
            text = "Part 2 - Solving"
        )

        is SolverStage2.Solved -> Title(
            icon = Res.drawable.check_mark,
            text = "Part 2 - Solved"
        )
    }

@Composable
private fun SolverStage2.resolveSectionOutlineColor(): Color =
    when (this) {
        is SolverStage2.Solving -> MaterialTheme.colorScheme.outline
        is SolverStage2.Solved -> Color(0xff98fb98)
    }

@Composable
private fun Dialer(
    markerPosition: Float,
    modifier: Modifier = Modifier,
) {
    val tetMeasurer = rememberTextMeasurer()
    val ringBrush = remember {
        Brush.linearGradient(
            0f to Color.White,
            1f to Color.Gray,
            end = Offset.Infinite.copy(x = 0f)
        )
    }
    val markerBrush = remember {
        Brush.radialGradient(
            0.5f to Color.White,
            1f to Color.Gray,
        )
    }
    val dialerBrush = remember {
        Brush.radialGradient(
            0f to Color.White,
            0.7f to Color.LightGray,
            1f to Color.Gray,
        )
    }
    Canvas(
        modifier
    ) {
        // Dial Ring
        val outerRingWidth = 0.1f * (size.minDimension / 2.0f)
        val middleRingWidth = 0.025f * (size.minDimension / 2.0f)
        val innerRingWidth = 0.05f * (size.minDimension / 2.0f)
        val outerRingRadius = size.minDimension / 2.0f
        val middleRingRadius = outerRingRadius - outerRingWidth
        val innerRingRadius = middleRingRadius - middleRingWidth
        drawCircle(
            brush = ringBrush,
            radius = outerRingRadius - outerRingWidth / 2f,
            style = Stroke(outerRingWidth)
        )
        drawCircle(
            color = Color.LightGray,
            radius = middleRingRadius - middleRingWidth / 2f,
            style = Stroke(middleRingWidth)
        )
        scale(scaleX = 1f, scaleY = -1f) {
            drawCircle(
                brush = ringBrush,
                radius = innerRingRadius - innerRingWidth / 2f,
                style = Stroke(innerRingWidth)
            )
        }

        // Marker
        val markerRadius = 0.02f * (size.minDimension / 2.0f)
        translate(
            top = -innerRingRadius + innerRingWidth / 2f,
        ) {
            scale(scaleX = markerRadius / (size.minDimension / 2f), scaleY = markerRadius / (size.minDimension / 2f)) {
                drawCircle(
                    brush = markerBrush,
                )
            }
        }

        // Dialer Gap
        val dialerGapWidth = 0.0125f * (size.minDimension / 2.0f)
        val dialerGapRadius = innerRingRadius - innerRingWidth
        drawCircle(
            color = Color.Black,
            radius = dialerGapRadius - dialerGapWidth / 2f,
            style = Stroke(dialerGapWidth)
        )

        // Dialer
        val dialerRadius = dialerGapRadius - dialerGapWidth
        drawCircle(
            brush = dialerBrush,
            radius = dialerRadius,
        )

        // Dialer Markers
        rotate(markerPosition / 100f * 360f) {
            val dialerMarkerRadius = dialerRadius - 0.025f * (size.minDimension / 2.0f)
            repeat(100) {
                val drawTextMarker = it % 5 == 0
                val zeroMarker = it == 0
                val curMarkerLength =
                    if (drawTextMarker) 0.1f * (size.minDimension / 2) else 0.025f * (size.minDimension / 2)
                translate(
                    size.center.x, size.center.y,
                ) {
                    rotate(
                        degrees = -it * 360 / 100f,
                        pivot = Offset.Zero
                    ) {
                        translate(
                            0f, -dialerMarkerRadius,
                        ) {
                            drawLine(
                                color = Color.Gray,
                                start = Offset.Zero,
                                end = Offset(0f, curMarkerLength),
                                strokeWidth = 3f * density,
                            )
                            drawLine(
                                color = Color.DarkGray,
                                start = Offset.Zero,
                                end = Offset(0f, curMarkerLength),
                                strokeWidth = 1f * density,
                            )

                            if (drawTextMarker) {
                                val textStyle =
                                    if (zeroMarker) TextStyle.Default.copy(fontWeight = FontWeight.Bold) else TextStyle.Default
                                val textLayoutResult = tetMeasurer.measure(
                                    text = "$it",
                                    style = textStyle
                                )
                                textLayoutResult.size
                                drawText(
                                    textLayoutResult,
                                    topLeft = Offset(
                                        -textLayoutResult.size.width / 2f,
                                        curMarkerLength + dialerGapWidth
                                    )
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DialMovement(
    dialMovement: DialMovement,
    modifier: Modifier = Modifier
) {
    Row(
        horizontalArrangement = spacedBy(16.dp, Alignment.CenterHorizontally),
        verticalAlignment = Alignment.CenterVertically,
        modifier = modifier,
    ) {
        Text(
            text = "Current Movement:",
            style = MaterialTheme.typography.titleLarge.copy(color = Color.White),
        )
        Row(
            horizontalArrangement = spacedBy(8.dp, Alignment.CenterHorizontally),
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .width(160.dp)
                .background(MaterialTheme.colorScheme.surfaceContainerHighest, shape = MaterialTheme.shapes.medium)
                .padding(8.dp)
        ) {
            Image(
                painter = painterResource(dialMovement.direction.toDrawable()),
                contentDescription = null,
                colorFilter = ColorFilter.tint(Color.White),
                modifier = Modifier.size(24.dp),
            )
            Text(
                text = dialMovement.steps.toString(),
                style = MaterialTheme.typography.titleLarge.copy(
                    color = Color.White,
                    fontFamily = Font(Res.font.source_code_pro_regular).toFontFamily()
                ),
            )
        }
    }
}

@Preview
@Composable
fun DialerPreview() {
    AoCTheme {
        Dialer(
            markerPosition = 50f,
            modifier = Modifier.fillMaxSize().background(Color.Black),
        )
    }
}

@Preview
@Composable
fun DialMovementPreview() {
    AoCTheme {
        Column(
            modifier = Modifier.fillMaxSize().background(Color.Black),
            verticalArrangement = spacedBy(16.dp),
        ) {
            DialMovement(
                dialMovement = DialMovement(Direction.Left, 88),
                modifier = Modifier.fillMaxWidth().background(Color.Black),
            )
            DialMovement(
                dialMovement = DialMovement(Direction.Right, 77),
                modifier = Modifier.fillMaxWidth().background(Color.Black),
            )
        }
    }
}
